import React from 'react';
import { User, Mail, Briefcase, ChevronRight, MessageSquare } from 'lucide-react';

const COUNTRY_CODES = [
  { code: '+1', flag: '\u{1F1FA}\u{1F1F8}', label: 'US' },
  { code: '+44', flag: '\u{1F1EC}\u{1F1E7}', label: 'UK' },
  { code: '+61', flag: '\u{1F1E6}\u{1F1FA}', label: 'AU' },
  { code: '+49', flag: '\u{1F1E9}\u{1F1EA}', label: 'DE' },
  { code: '+33', flag: '\u{1F1EB}\u{1F1F7}', label: 'FR' },
  { code: '+91', flag: '\u{1F1EE}\u{1F1F3}', label: 'IN' },
  { code: '+81', flag: '\u{1F1EF}\u{1F1F5}', label: 'JP' },
  { code: '+55', flag: '\u{1F1E7}\u{1F1F7}', label: 'BR' },
  { code: '+52', flag: '\u{1F1F2}\u{1F1FD}', label: 'MX' },
];

const ContactForm = ({ formData, setFormData, countryCode, setCountryCode, onSubmit, onSkip, isSubmitting, error, theme, themeVars }) => {
  const t = theme;
  const { isDark, subtleBg, subtleBorder, mutedText } = themeVars;

  return (
    <div className="rounded-2xl p-6 card-form-section" style={{ background: subtleBg, border: `1px solid ${subtleBorder}` }} data-testid="public-card-form-section">
      <p className="text-xs font-semibold uppercase tracking-wider text-center mb-5" style={{ color: mutedText }}>Share your info</p>
      <form onSubmit={onSubmit} className="space-y-3" data-testid="public-contact-form">
        <div className="relative">
          <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: mutedText }} />
          <input type="text" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} required placeholder="Full name"
            className="w-full rounded-lg pl-11 pr-4 py-3 text-sm font-medium outline-none"
            style={{ background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)', border: `1px solid ${subtleBorder}`, color: t.text_color }}
            data-testid="public-input-name" />
        </div>
        <div className="flex gap-0">
          <select value={countryCode} onChange={e => setCountryCode(e.target.value)}
            className="border border-r-0 rounded-l-lg pl-2 pr-1 py-3 text-sm font-medium cursor-pointer"
            style={{ background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)', borderColor: subtleBorder, color: t.text_color, minWidth: '72px' }}
            data-testid="public-country-code">
            {COUNTRY_CODES.map((c, i) => <option key={i} value={c.code}>{c.flag} {c.code}</option>)}
          </select>
          <input type="tel" value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} required placeholder="Phone number"
            className="flex-1 rounded-r-lg pl-3 pr-4 py-3 text-sm font-medium outline-none"
            style={{ background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)', border: `1px solid ${subtleBorder}`, color: t.text_color }}
            data-testid="public-input-phone" />
        </div>
        <div className="relative">
          <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: mutedText }} />
          <input type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} placeholder="Email (optional)"
            className="w-full rounded-lg pl-11 pr-4 py-3 text-sm font-medium outline-none"
            style={{ background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)', border: `1px solid ${subtleBorder}`, color: t.text_color }}
            data-testid="public-input-email" />
        </div>
        <div className="relative">
          <Briefcase className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: mutedText }} />
          <input type="text" value={formData.company} onChange={e => setFormData({...formData, company: e.target.value})} placeholder="Company (optional)"
            className="w-full rounded-lg pl-11 pr-4 py-3 text-sm font-medium outline-none"
            style={{ background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)', border: `1px solid ${subtleBorder}`, color: t.text_color }}
            data-testid="public-input-company" />
        </div>
        <div className="relative">
          <MessageSquare className="absolute left-3.5 top-3.5 w-4 h-4" style={{ color: mutedText }} />
          <textarea value={formData.notes} onChange={e => setFormData({...formData, notes: e.target.value})} placeholder="Notes (optional)" rows={2}
            className="w-full rounded-lg pl-11 pr-4 py-3 text-sm font-medium outline-none resize-none"
            style={{ background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)', border: `1px solid ${subtleBorder}`, color: t.text_color }}
            data-testid="public-input-notes" />
        </div>

        {error && <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-2 text-sm text-center rounded-lg" data-testid="public-form-error">{error}</div>}

        <button type="submit" disabled={isSubmitting}
          className="w-full py-3 rounded-full font-semibold text-sm transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2"
          style={{ background: t.primary_color, color: isDark ? t.bg_color : '#ffffff' }}
          data-testid="public-submit-button">
          {isSubmitting ? 'Connecting...' : 'Connect'}
          {!isSubmitting && <ChevronRight className="w-4 h-4" />}
        </button>
      </form>
      <button onClick={onSkip}
        className="w-full text-xs uppercase tracking-wider mt-4 py-2 font-semibold transition-opacity hover:opacity-70"
        style={{ color: mutedText }}
        data-testid="public-skip-button">
        Skip to links
      </button>
    </div>
  );
};

export default ContactForm;
